/**
 * Geocoding against the public Nominatim instance (#547).
 *
 * The OSMF usage policy allows user-triggered searches at no more than one
 * request per second and forbids search-as-you-type. So every call here is
 * bound to an explicit action (Enter, the search button, a pin drop), and a
 * small gate spaces the calls out. A result list instead of a blind first hit
 * is what the policy leaves room for, and it is what fixes the wrong-house
 * problem: the creator picks the match.
 */

export interface GeocodeResult {
  lat: number;
  lng: number;
  displayName: string;
}

export interface GeocodeBias {
  lat: number;
  lng: number;
}

const BASE = 'https://nominatim.openstreetmap.org';
const MIN_INTERVAL_MS = 1100;
const MAX_RESULTS = 5;
/** Half width of the preference box around the bias point, in degrees. */
const BIAS_HALF_SPAN = 0.25;

let lastRequestAt = 0;

/** Spaces requests so the instance sees at most one per second from this tab. */
async function gate(): Promise<void> {
  const wait = lastRequestAt + MIN_INTERVAL_MS - Date.now();
  if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
  lastRequestAt = Date.now();
}

function toResult(item: { lat: string; lon: string; display_name: string }): GeocodeResult {
  return { lat: Number(item.lat), lng: Number(item.lon), displayName: item.display_name };
}

/**
 * Up to five matches for a free-text query. With a bias point the box around
 * it is preferred but not enforced, so an address in another town still
 * resolves when the creator spells it out.
 */
export async function forwardGeocode(query: string, bias?: GeocodeBias | null): Promise<GeocodeResult[]> {
  const q = query.trim();
  if (!q) return [];
  const params = new URLSearchParams({
    format: 'jsonv2',
    limit: String(MAX_RESULTS),
    addressdetails: '0',
    q,
  });
  if (bias) {
    const left = bias.lng - BIAS_HALF_SPAN;
    const right = bias.lng + BIAS_HALF_SPAN;
    const top = bias.lat + BIAS_HALF_SPAN;
    const bottom = bias.lat - BIAS_HALF_SPAN;
    params.set('viewbox', `${left},${top},${right},${bottom}`);
    params.set('bounded', '0');
  }
  await gate();
  const r = await fetch(`${BASE}/search?${params.toString()}`, { headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error(`geocode ${r.status}`);
  const data = (await r.json()) as Array<{ lat: string; lon: string; display_name: string }>;
  // Nominatim returns one entry per OSM object, so a street shows up once per
  // segment with the same name. The creator only needs one of each.
  const seen = new Set<string>();
  return data.map(toResult).filter((result) => {
    if (seen.has(result.displayName)) return false;
    seen.add(result.displayName);
    return true;
  });
}

/** Address text for a point, or null when the instance knows none. */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  await gate();
  const r = await fetch(`${BASE}/reverse?format=jsonv2&lat=${lat}&lon=${lng}`, {
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) return null;
  const data = (await r.json()) as { display_name?: string };
  return data.display_name ?? null;
}

/** Test hook: forget the last request time so gated calls run immediately. */
export function resetGeocodeGate(): void {
  lastRequestAt = 0;
}
